import {
    ISimplePresentation,
    ISimplePresentationRunnerController
} from "../../core/winpresentation/SimplePresentationRunner";
import {gsap} from "gsap";
import {KennethMustDie} from "../KennethMustDie";
import {KMDSymbol} from "../reels/KMDSymbol";
import {IBetWayWinData, SPECIAL_SYMBOL_NAMES, X_NUDGE_ANIMATIONS} from "../server/IKMDData";
import {SimpleReelModel} from "../../core/reel/simplereel/SimpleReelModel";
import {KMDSound} from "../sound/KMDSound";

export class KMDM1M2SparkRunner implements ISimplePresentation {

    public shouldRun(): boolean {
        return (KennethMustDie.model.gameData.symbolLines && KennethMustDie.model.gameData.symbolLines.length > 0);
    }

    public start(controller: ISimplePresentationRunnerController): void {
        const tl: gsap.core.Timeline = gsap.timeline({
                delay: 0.2,
                onComplete: () => {
                    controller.finish();
                }
            }),
            symbolLines: number[] = KennethMustDie.model.gameData.symbolLines,
            evalArea: string[][] = KennethMustDie.model.gameData.evaluatedArea,
            numSymbols: number = 4;

        controller.setSkipCallback();

        tl.add(() => {
            this.sendSymbolsToNormal(symbolLines, numSymbols, evalArea);
        });

        tl.to(this, {
            duration: 0.1
        });

        tl.add([
            this.transformAction(symbolLines, numSymbols, evalArea),
            this.heartOverlay(symbolLines, numSymbols, evalArea)
        ]);
    }

    private sendSymbolsToNormal(symbolLines: number[], numSymbols: number, evalArea: string[][]): void {
        const betWayWins: IBetWayWinData[] = KennethMustDie.model.gameData.betWayWins;

        betWayWins.forEach((betWayWin: IBetWayWinData) => {
            betWayWin.reelWinPositions.forEach((value: boolean[], reelId: number) => {
                value.forEach((val: boolean, symId: number) => {
                    if (val && evalArea[reelId][symId] !== SPECIAL_SYMBOL_NAMES.NW) { // if a position is NudgeWild in eval area, no need to change it, as NW can't be on noWin state
                        const reel: SimpleReelModel = (symId === 3 ? KennethMustDie.specialReelView.reel : KennethMustDie.reelView.reelModels[reelId]),
                            effectiveSymId: number = (reelId > 0 && reelId < 4 ? (reel.id === 5 ? (reelId - 1) : symId) : (symId - 1)),
                            symbol: KMDSymbol = (reel.getStopSymbolAt(effectiveSymId).positionable as KMDSymbol);

                        symbol.setToNormalState();

                        KennethMustDie.reelAreaSymbolsName[reelId][symId] = symbol.name; // consumed in method handleM1M2SparkFeature of KMDWinRunner
                    }
                });
            });
        });

        if (betWayWins.length) {
            symbolLines.forEach((value: number) => {
                const reelId: number = Math.floor(value / numSymbols),
                    symId: number = (value % numSymbols - ((reelId === 4 || reelId === 0) ? 1 : 0)),
                    symbol: KMDSymbol = (KennethMustDie.reelView.reelModels[reelId].getStopSymbolAt(symId).positionable as KMDSymbol);

                if (evalArea[reelId][symId] !== SPECIAL_SYMBOL_NAMES.NW) { // if a position is NudgeWild in eval area, no need to change it, as NW can't be on noWin state
                    symbol.setToNormalState();
                }
            });
        }
    }

    private transformAction(symbolLines: number[], numSymbols: number, evalArea: string[][]): gsap.core.Timeline {
        const convertTo: string = KennethMustDie.model.gameData.sM1M2TransformSym,
            timeline: gsap.core.Timeline = gsap.timeline();
        let prevReelId: number = -1;

        // #461
        timeline.add(() => {
            KennethMustDie.soundPlayer.playSound(KMDSound.WIN_SCRAMBLE);
        }, 0);

        symbolLines.forEach((value: number) => {
            const reelId: number = Math.floor(value / numSymbols),
                symId: number = (value % numSymbols - ((reelId === 4 || reelId === 0) ? 1 : 0)),
                reel: SimpleReelModel = KennethMustDie.reelView.reelModels[reelId],
                symbol: KMDSymbol = (reel.getStopSymbolAt(symId).positionable as KMDSymbol),
                multi: number = KennethMustDie.model.gameData.multiplier[reelId][symId + ((reelId === 4 || reelId === 0) ? 1 : 0)];

            if (evalArea[reelId][symId] === SPECIAL_SYMBOL_NAMES.NW) {
                if (convertTo !== SPECIAL_SYMBOL_NAMES.W && reelId !== prevReelId) {// keep the check here, no need to go to else part of above condition in such cases
                    timeline.add(this.transformXNudgeSymbol(evalArea, reelId, reel, convertTo), 0);
                }

                prevReelId = reelId;
            } else if (symbol.name !== SPECIAL_SYMBOL_NAMES.W && !KennethMustDie.stickyWilds.checkIfSticky(reelId, symId)) { // #430
                timeline.add(symbol.playOverlayAnimation("flicker"), 0);
                timeline.add(() => {
                    symbol.updateTexture(convertTo, multi);
                }, "-=0.1");
            }
        });

        return timeline;
    }

    private transformXNudgeSymbol(evalArea: string[][], reelId: number, reel: SimpleReelModel, convertTo: string): gsap.core.Timeline {
        let xNudgeSymbol: KMDSymbol = (reel.getStopSymbolAt(1).positionable as KMDSymbol),
            state: string = X_NUDGE_ANIMATIONS.UP_1_STEP;

        if (evalArea[reelId][3] === SPECIAL_SYMBOL_NAMES.NW) {
            xNudgeSymbol = (KennethMustDie.specialReelView.reel.getStopSymbolAt((reelId - 1)).positionable as KMDSymbol);
            state = X_NUDGE_ANIMATIONS.UP_3_STEP;
        } else if (evalArea[reelId][2] === SPECIAL_SYMBOL_NAMES.NW) {
            xNudgeSymbol = (reel.getStopSymbolAt(2).positionable as KMDSymbol);
            state = X_NUDGE_ANIMATIONS.UP_2_STEP;
        }

        if (xNudgeSymbol.m1m2NameHelperXNudge === X_NUDGE_ANIMATIONS.HELPER_300) {
            state = X_NUDGE_ANIMATIONS.UP_1_STEP;
        } else if (xNudgeSymbol.m1m2NameHelperXNudge === X_NUDGE_ANIMATIONS.FULL_200) {
            state = X_NUDGE_ANIMATIONS.FULL_200;
        }
        state += (convertTo === "M1" ? X_NUDGE_ANIMATIONS.SM1_HELPER : X_NUDGE_ANIMATIONS.SM2_HELPER);

        xNudgeSymbol.m1m2NameHelperXNudge = state;

        return xNudgeSymbol.setState(state);
    }

    private heartOverlay(symbolLines: number[], numSymbols: number, evalArea: string[][]): gsap.core.Timeline {
        const timeline: gsap.core.Timeline = gsap.timeline();
        let prevReelId: number = -1;

        symbolLines.forEach((value: number) => {
            const reelId: number = Math.floor(value / numSymbols),
                symId: number = (value % numSymbols - ((reelId === 4 || reelId === 0) ? 1 : 0)),
                reel: SimpleReelModel = KennethMustDie.reelView.reelModels[reelId],
                symbol: KMDSymbol = (reel.getStopSymbolAt(symId).positionable as KMDSymbol);

            if (evalArea[reelId][symId] === SPECIAL_SYMBOL_NAMES.NW) {
                if (reelId !== prevReelId) {// keep the check here, no need to go to else part of above condition in such cases
                    let heartsLength: number = 2;

                    if (evalArea[reelId][3] === SPECIAL_SYMBOL_NAMES.NW) {
                        heartsLength = 3;

                        timeline.add((KennethMustDie.specialReelView.reel.getStopSymbolAt((reelId - 1)).positionable as KMDSymbol).playHeartOverlayAnimation(), 0);
                    } else if (evalArea[reelId][2] === SPECIAL_SYMBOL_NAMES.NW) {
                        heartsLength = 3;
                    }

                    for (let ctr: number = 0; ctr < heartsLength; ctr++) {
                        const reelSym: KMDSymbol = (reel.getStopSymbolAt(ctr).positionable as KMDSymbol);

                        timeline.add(reelSym.playHeartOverlayAnimation(), 0);
                    }

                    prevReelId = reelId;
                }
            } else {
                timeline.add(symbol.playHeartOverlayAnimation(), 0);
            }
        });

        return timeline;
    }
}